import { lazy, Suspense, useMemo } from "react";
import {
    createBrowserRouter,
    Navigate,
    Outlet,
    RouteObject,
    RouterProvider,
    useLocation,
    useParams,
    useSearchParams,
} from "react-router";

import { Spinner } from "./components/ui/spinner";
import { AuthRouteGuard } from "./auth-route-guard";
import AccessGuard from "./components/access-guard";
import {
    CUSTOMER_DEVICES_FILTER_STORAGE_KEY,
    FeatureFlag,
    FILTER_STATE_STORAGE_PREFIX,
    MANAGE_CUSTOMERS_DEVICES_FILTER_STORAGE_KEY,
} from "./constants";
import ErrorBoundary from "./components/ui/error-boundary";
import Navbar from "./components/ui/navbar";
import { LDFlagSet, useFlags } from "launchdarkly-react-client-sdk";
import { getUpdatedPath } from "./route-helpers";
import {
    BreadcrumbsProvider,
    DeviceGroupBreadcrumbSetter,
} from "./contexts/useBreadcrumbs";
import { ThemeProvider } from "./contexts/theme-provider";
import { SitesSidebar } from "./pages/sites/sidebar";
import { SearchSidebar } from "./pages/search/sidebar";
import { SettingsSidebar } from "./pages/settings/sidebar";
import { ManageCustomersSidebar } from "./pages/manage-customers/sidebar";
import { SystemSidebar } from "./pages/system/sidebar";
import { HelpSidebar } from "./pages/help/sidebar";
import { faultLinks, NetworkSidebar } from "./pages/network/sidebar";
import {
    PageLayout,
    RootLayout,
    SidebarLayout,
    SidebarStacker,
} from "./components/ui/layouts";
import { IlluminateUserPermissionsEnum } from "./api/openapi";
import { OrganisationSidebar } from "./components/organisation-sidebar";
import SiteRouteFixer from "./pages/device-page/site-route-fixer";
import { DevicesSidebar } from "./pages/devices/sidebar";
import { DeviceGroupSidebar } from "./components/device-group-sidebar";
import { useIsCustomer } from "./hooks/useIsCustomer";
import { useDeviceGroupDetail } from "./api/device-group-http-functions";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { ManageFleetSidebar } from "./pages/manage-fleet/sidebar";
import { OnboardingTourProvider } from "./onboarding/onboarding-tour-context";

const Dashboard = lazy(() => import("./pages/dashboard/dashboard"));
const DebugConsole = lazy(() => import("./pages/device-page/debug-console"));
const SessionDashboard = lazy(() => import("./pages/dashboard/dashboard"));
const ServiceModelsPage = lazy(
    () => import("./pages/service-model-list-page/service-models-page"),
);
const VehicleModelCatalogPage = lazy(
    () =>
        import("./pages/vehicle-model-catalog-page/vehicle-model-catalog-page"),
);
const DeviceListForDeviceGroup = lazy(
    () =>
        import("./pages/device-list-for-device-group/device-list-for-device-group"),
);
const DeviceMessageHistory = lazy(
    () =>
        import("./pages/device-page/device-history/device-message-history/device-message-history"),
);
const NoAuthDeprecated = lazy(
    () => import("./components/no-auth/no-auth-deprecated"),
);
const NoAuth = lazy(() => import("./components/no-auth/no-auth"));
const NoSideMenuLayout = lazy(
    () => import("./components/layout/no-side-menu-layout"),
);
const DeviceStatusHistory = lazy(() =>
    import("./pages/device-status-history/device-status-history").then((m) => ({
        default: m.DeviceStatusHistory,
    })),
);
const ConnectivityHistoryPage = lazy(
    () => import("./pages/connectivity-history-page/connectivity-history-page"),
);

const CoreConfigPageDeprecated = lazy(
    () => import("@/pages/core-config/core-config-page-deprecated"),
);

const CoreConfigurationPage = lazy(
    () => import("@/pages/core-config/core-configuration-page"),
);

const ConfigTreePage = lazy(
    () => import("@/pages/load-management/config/config-tree-page"),
);

const SiteDiagramPage = lazy(
    () => import("@/pages/core-config/site-diagram-page/site-diagram-page"),
);

const CoreConfigChangelogPage = lazy(
    () =>
        import("@/pages/core-config/core-config-changelog-page/core-config-changelog-page"),
);

const CoreConfigPreviewPage = lazy(
    () =>
        import("@/pages/core-config/core-config-preview-page/core-config-preview-page"),
);

const AsatTreePage = lazy(
    () => import("@/pages/load-management/asat/asat-tree-page"),
);

const CoreChargerLogsPage = lazy(
    () => import("@/pages/core-charger-logs-page/core-charger-logs-page"),
);

const DeviceConfigurationPage = lazy(
    () => import("@/pages/device-configuration/device-configuration-page"),
);
const TransactionPage = lazy(
    () => import("@/pages/transaction-page/transaction-page"),
);

const DeviceGroupPage = lazy(
    () => import("@/pages/device-group-page/device-group-page"),
);

const AccountInfoPage = lazy(
    () => import("@/pages/account-info-page/account-info-page"),
);
const DevicesPage = lazy(() => import("@/pages/devices-page/devices-page"));
const ErrorPage = lazy(() => import("@/pages/error"));
const LoggedOutPage = lazy(() => import("@/pages/logout"));
const NotFoundPage = lazy(
    () => import("@/pages/page-not-found/page-not-found"),
);
const DeviceOwnerPage = lazy(
    () => import("@/pages/device-owner-page/device-owner-page"),
);
const FirmwareLibraryPage = lazy(
    () => import("@/pages/firmware-library-page/firmware-library-page"),
);
const DevicePage = lazy(() => import("@/pages/device-page/device-page"));
const RfidTagListPage = lazy(
    () => import("@/pages/rfid-tag-list-page/rfid-tag-list-page"),
);
const RfidTagListPageDeprecated = lazy(
    () => import("@/pages/rfid-tag-list-page/rfid-tag-list-page-deprecated"),
);
const RfidTagTablePage = lazy(
    () => import("@/pages/rfid-tag-list-page/rfid-tag-table-page"),
);
const DeviceCatalogueTablePage = lazy(
    () =>
        import("@/pages/device-catalogue-list-page/device-catalogue-table-page"),
);
const OfferTablePage = lazy(
    () => import("@/pages/offer-list-page/offer-table-page"),
);
const RfidTagDetailPage = lazy(
    () => import("@/pages/rfid-tag-detail-page/rfid-tag-detail-page"),
);
const RfidTagRedirect = lazy(
    () => import("@/pages/rfid-tag-list-page/rfid-tag-redirect"),
);
const UsersListPage = lazy(
    () => import("@/pages/users-list-page/users-list-page"),
);
const PassthroughConfigsListPage = lazy(
    () =>
        import("@/pages/passthrough-config-list-page/passthrough-configs-list-page"),
);
const MaintenanceDashboardPage = lazy(
    () =>
        import("@/pages/maintenance-dashboard-page/maintenance-dashboard-page"),
);
const Adb2cSignupPage = lazy(
    () => import("@/pages/staff-login-page/adb2c-signup-page"),
);
const CentralSystemsListPage = lazy(
    () => import("@/pages/central-systems-list-page/central-systems-list-page"),
);
const RolesList = lazy(() => import("@/pages/roles-list"));
const DeviceScheduleTemplatesList = lazy(
    () => import("@/pages/deviece-schedule-templates-list"),
);
const UserActionLogList = lazy(
    () => import("@/pages/audit-logs/user-action-log-list"),
);
const PaymentLogList = lazy(
    () => import("@/pages/audit-logs/payment-log-list"),
);
const WebhooksListPage = lazy(
    () => import("@/pages/webhooks/webhooks-list-page"),
);
const DeviceOwnersPage = lazy(
    () => import("@/pages/device-owners-page/device-owners-page"),
);
const DeviceGroupsListPage = lazy(
    () => import("@/pages/device-groups-list-page/device-groups-list-page"),
);
const NoMfaLoginPage = lazy(
    () => import("@/pages/no-mfa-login/no-mfa-login-page"),
);
const StaffLoginPage = lazy(
    () => import("@/pages/staff-login-page/staff-login-page"),
);
const SustainabilityDashboardPage = lazy(
    () =>
        import("@/pages/sustainability-dashboard-page/sustainability-dashboard-page"),
);
const AdhocReportsPage = lazy(
    () => import("@/pages/webhooks/adhoc-reports/adhoc-reports-page"),
);
const FaultsDashboardPageV2 = lazy(
    () =>
        import("@/pages/faults/deprecated/faults-dashboard-page-v2/FaultsDashboardPageV2"),
);
const FaultedDeviceTable = lazy(
    () => import("@/pages/faults/faulted-device-table"),
);
const WarningsTable = lazy(() => import("@/pages/faults/warnings-table"));
const DeviceStatusDetailPage = lazy(
    () => import("@/pages/faults/details/device-status-detail-page"),
);
const WarningDetailPage = lazy(
    () => import("@/pages/faults/details/warning-detail-page"),
);

const TariffPage = lazy(() => import("@/pages/tariffs-page/tariff-page"));
const MaintenanceTicketsPage = lazy(
    () => import("@/pages/maintenance-tickets-page/maintenance-tickets-page"),
);
const ServiceEventList = lazy(
    () => import("@/components/service-event-list/service-event-list"),
);
const TariffDetailsPage = lazy(
    () => import("@/pages/tariffs-details-page/tariff-details-page"),
);
const CreateTariffPage = lazy(
    () => import("@/pages/tariffs-create-page/create-tariff-page"),
);
const EditTariffPage = lazy(
    () => import("@/pages/tariffs-edit-page/edit-tariff-page"),
);
const TransactionSearchPage = lazy(
    () => import("./components/device-transactions/transaction-search-page"),
);
const HelpTopicPage = lazy(() => import("@/pages/help/help-topic"));
const PreviewRoleRedirect = lazy(
    () => import("@/pages/roles-list/preview-role-redirect"),
);

const NotificationPreferencePage = lazy(
    () =>
        import("@/pages/notification-preference-page/notification-preference-page"),
);
const CrudNotificationPreferencePage = lazy(
    () =>
        import("@/pages/crud-notification-preference-page/crud-notification-preference-page"),
);

const HomePage = lazy(() => import("@/pages/home-page/home-page"));
const TransactionListPage = lazy(
    () => import("@/pages/transaction-list-page/transaction-list-page"),
);
const OrganisationPreferencesPage = lazy(
    () =>
        import("@/pages/organisation-preferences-page/organisation-preferences-page"),
);

const VehiclesPage = lazy(() => import("@/pages/vehicles-page/vehicles-page"));
const VehiclePage = lazy(() => import("@/pages/vehicle-page/vehicle-page"));

const wrapInErrorBoundaries = (routes: RouteObject[]): RouteObject[] => {
    return routes.map((route) => ({
        ...route,
        errorElement: <ErrorBoundary />,
        children: route.children
            ? wrapInErrorBoundaries(route.children)
            : undefined,
    })) as RouteObject[];
};

const RouteRedirectHandler = ({ children }: { children: React.ReactNode }) => {
    const location = useLocation();

    // Never redirect the root path or if path is already the same
    if (location.pathname === "/" || !location.pathname) {
        return children;
    }

    // Check if this is a route that needs redirecting (old routes or /x routes)
    const newPath = getUpdatedPath(location.pathname);

    if (newPath && newPath !== location.pathname) {
        return (
            <Navigate
                to={{
                    ...location,
                    pathname: newPath,
                }}
                replace
            />
        );
    }

    return children;
};

export const DEVICE_FILTER_OVERRIDE_KEY = "device-list-override-key";
const DeviceGroupRedirect = () => {
    const { deviceGroupTag, "*": restParams } = useParams<{
        deviceGroupTag: string;
        "*"?: string;
    }>();
    const isCustomer = useIsCustomer();
    const { data: deviceGroup, isLoading } = useDeviceGroupDetail(
        deviceGroupTag!,
        { query: { enabled: !!deviceGroupTag } },
    );

    const [searchParams] = useSearchParams();

    const stringifiedSearchParams = searchParams.toString();

    if (isLoading) {
        return <Spinner sizing="largest" />;
    } else if (deviceGroup?.deviceGroupType === "Organisation") {
        if (isCustomer) {
            const searchString = stringifiedSearchParams.replace(
                DEVICE_FILTER_OVERRIDE_KEY,
                `${FILTER_STATE_STORAGE_PREFIX}-${CUSTOMER_DEVICES_FILTER_STORAGE_KEY}`,
            );
            return (
                <Navigate
                    to={{
                        pathname: `/${restParams}`,
                        search: searchString,
                    }}
                    replace
                />
            );
        }
        const searchString = stringifiedSearchParams.replace(
            DEVICE_FILTER_OVERRIDE_KEY,
            `${FILTER_STATE_STORAGE_PREFIX}-devices-page-${deviceGroupTag}`,
        );
        return (
            <Navigate
                to={{
                    pathname: `/manage-customers/organisations/${deviceGroupTag}/${restParams}`,
                    search: searchString,
                }}
                replace
            />
        );
    } else if (deviceGroup?.deviceGroupType === "Site") {
        if (isCustomer) {
            return (
                <Navigate
                    to={`/sites/${deviceGroupTag}/${restParams}`}
                    replace
                />
            );
        } else {
            return (
                <Navigate
                    to={`/manage-customers/sites/${deviceGroupTag}/${restParams}`}
                    replace
                />
            );
        }
    } else if (isCustomer) {
        return <Navigate to={`/${restParams}`} replace />;
    } else {
        return (
            <Navigate
                to={`/manage-customers/device-groups/${deviceGroupTag}/${restParams}`}
                replace
            />
        );
    }
};

const faultDeviceCategories = [
    {
        path: "faulted-devices",
        breadcrumbDisplay: "Faulted devices",
        faultType: "faulted" as const,
    },
    {
        path: "offline-devices",
        breadcrumbDisplay: "Offline devices",
        faultType: "offline" as const,
    },
    {
        path: "unknown-devices",
        breadcrumbDisplay: "Unknown devices",
        faultType: "unknown" as const,
    },
];

// Always registered — see IL-4187. Redirects to the V2 faults dashboard when
// on; mimics today's off-branch behaviour (no index route exists, so an
// unmatched hit 404s) when off. Reads useFlags() itself at render time rather
// than a flags snapshot captured at router-construction time.
const FaultsIndexContent = () => {
    const flags = useFlags();
    return flags[FeatureFlag.IL_4026_UPLIFT_FAULTS_DASHBOARD] ? (
        <Navigate to="/network/faulted-devices" replace />
    ) : (
        <NotFoundPage />
    );
};

const router = (flags: LDFlagSet) =>
    createBrowserRouter(
        wrapInErrorBoundaries([
            {
                element: (
                    <>
                        <Sonner
                            swipeDirections={[]}
                            visibleToasts={100}
                            duration={6000}
                        />
                        <RouteRedirectHandler>
                            <BreadcrumbsProvider>
                                <ThemeProvider>
                                    <Outlet />
                                </ThemeProvider>
                            </BreadcrumbsProvider>
                        </RouteRedirectHandler>
                    </>
                ),
                children: [
                    // Logged out routes
                    {
                        element: <NoSideMenuLayout logInOutPage />,
                        children: [
                            {
                                path: "/login",
                                element: flags[
                                    FeatureFlag.IL_4194_POWER_MOVE
                                ] ? (
                                    <NoAuth
                                        title1={"Welcome to Illuminate"}
                                        title2={
                                            "Please log in to manage your charging network."
                                        }
                                        desiredDirectory={"adb2c"}
                                    />
                                ) : (
                                    <NoAuthDeprecated
                                        title1={"WELCOME TO"}
                                        title2={"Illuminate"}
                                        title3={
                                            "Charging platform of the future."
                                        }
                                        message={
                                            "You are currently not logged in."
                                        }
                                        messageSpan={
                                            "Please log in to continue"
                                        }
                                        desiredDirectory={"adb2c"}
                                    />
                                ),
                            },
                            { path: "/staff", element: <StaffLoginPage /> },
                            { path: "/no-mfa", element: <NoMfaLoginPage /> },
                            { path: "/signup", element: <Adb2cSignupPage /> },
                            { path: "/logout", element: <LoggedOutPage /> },
                        ],
                    },
                    {
                        element: <NoSideMenuLayout />,
                        children: [
                            { path: "/error", element: <ErrorPage /> },
                            {
                                path: "*",
                                element: <NotFoundPage />,
                                errorElement: <></>,
                            },
                        ],
                    },
                    // Logged in routes
                    {
                        // Revised Top level navigation
                        element: <AuthRouteGuard />,
                        children: [
                            {
                                element: (
                                    <AccessGuard
                                        accessTags={["*"]} // Important to leave this to ensure error if can't fetch user
                                        unauthorizedBehaviour="unauthorizedPage"
                                        showFullScreenLoadSpinner
                                    >
                                        <RootLayout>
                                            <OnboardingTourProvider>
                                                <Navbar />
                                                <Outlet />
                                            </OnboardingTourProvider>
                                        </RootLayout>
                                    </AccessGuard>
                                ),
                                children: [
                                    {
                                        index: true,
                                        element: (
                                            <AccessGuard
                                                unauthorizedBehaviour="unauthorizedPage"
                                                permissions={[
                                                    "global_dashboard",
                                                ]}
                                            >
                                                <PageLayout>
                                                    <HomePage />
                                                </PageLayout>
                                            </AccessGuard>
                                        ),
                                    },
                                    {
                                        path: "device-group-redirect/:deviceGroupTag/*",
                                        element: <DeviceGroupRedirect />,
                                    },
                                    {
                                        path: "rfid-tag-redirect/:idTag",
                                        element: <RfidTagRedirect />,
                                    },
                                    {
                                        path: "help",
                                        handle: {
                                            breadcrumbDisplay: "Help Topics",
                                        },
                                        element: (
                                            <SidebarLayout>
                                                <SidebarStacker
                                                    key="help"
                                                    id="help"
                                                    ranking={0}
                                                    component={HelpSidebar}
                                                />
                                                <Outlet />
                                            </SidebarLayout>
                                        ),
                                        children: [
                                            {
                                                index: true,
                                                element: (
                                                    <Navigate
                                                        replace
                                                        to={"uptime-dashboard"}
                                                    />
                                                ),
                                            },
                                            {
                                                path: ":slug",
                                                handle: {
                                                    breadcrumbDisplay:
                                                        "Help Topic",
                                                },
                                                element: <HelpTopicPage />,
                                            },
                                        ],
                                    },
                                    {
                                        path: "network",
                                        handle: {
                                            breadcrumbDisplay: "My network",
                                        },
                                        element: (
                                            <SidebarLayout>
                                                <SidebarStacker
                                                    key="network"
                                                    id="network"
                                                    ranking={0}
                                                    component={NetworkSidebar}
                                                />
                                                <Outlet />
                                            </SidebarLayout>
                                        ),

                                        children: [
                                            {
                                                index: true,
                                                element: (
                                                    <Navigate
                                                        to={"uptime"}
                                                        replace
                                                    />
                                                ),
                                            },
                                            {
                                                path: "uptime/:orgId?/:siteId?/:tabId?",
                                                handle: {
                                                    breadcrumbDisplay: "Uptime",
                                                },
                                                element: (
                                                    <Dashboard type="uptime" />
                                                ),
                                            },
                                            {
                                                path: "sessions",
                                                handle: {
                                                    breadcrumbDisplay:
                                                        "Sessions",
                                                },
                                                element: (
                                                    <AccessGuard
                                                        featureFlags={[
                                                            FeatureFlag.IL_3114_CSR,
                                                        ]}
                                                        flagOffElement={null}
                                                        unauthorizedBehaviour="unauthorizedPage"
                                                    >
                                                        <SessionDashboard type="session" />
                                                    </AccessGuard>
                                                ),
                                            },
                                            {
                                                path: "sustainability/:orgId?/:siteId?",
                                                handle: {
                                                    breadcrumbDisplay:
                                                        "Sustainability",
                                                },
                                                element: (
                                                    <SustainabilityDashboardPage />
                                                ),
                                            },
                                            // Always registered — see IL-4187. Content/access decisions are
                                            // made live inside each element (AccessGuard's own useFlags()
                                            // read), never from route existence, so a hit can't transiently
                                            // 404 before LaunchDarkly's identify() resolves.
                                            {
                                                path: "faults",
                                                handle: {
                                                    breadcrumbDisplay: "Faults",
                                                },
                                                children: [
                                                    {
                                                        index: true,
                                                        element: (
                                                            <FaultsIndexContent />
                                                        ),
                                                    },
                                                    ...faultLinks.map(
                                                        ({
                                                            display,
                                                            tabId,
                                                            children,
                                                        }) => ({
                                                            path: tabId.toLowerCase(),
                                                            handle: {
                                                                breadcrumbDisplay:
                                                                    display,
                                                            },
                                                            // Legacy V2-off tab content. When the V2 faults
                                                            // dashboard is on, this (and any bookmarked deep
                                                            // link under it) shows as unauthorized instead of
                                                            // silently reviving the old dashboard, so users
                                                            // are pushed toward the new UI.
                                                            element: (
                                                                <AccessGuard
                                                                    unauthorizedBehaviour="unauthorizedPage"
                                                                    featureFlags={[
                                                                        FeatureFlag.IL_4026_UPLIFT_FAULTS_DASHBOARD,
                                                                    ]}
                                                                    featureFlagsSelector="none"
                                                                >
                                                                    <Outlet />
                                                                </AccessGuard>
                                                            ),
                                                            children: [
                                                                {
                                                                    index: true,
                                                                    element: (
                                                                        <AccessGuard
                                                                            unauthorizedBehaviour="unauthorizedPage"
                                                                            accessTags={[
                                                                                "*",
                                                                            ]}
                                                                            permissions={[
                                                                                "global_dashboard",
                                                                            ]}
                                                                        >
                                                                            <FaultsDashboardPageV2
                                                                                tab={
                                                                                    tabId
                                                                                }
                                                                            />
                                                                        </AccessGuard>
                                                                    ),
                                                                },
                                                                ...children,
                                                            ],
                                                        }),
                                                    ),
                                                ],
                                            },
                                            ...faultDeviceCategories.map(
                                                ({
                                                    path,
                                                    breadcrumbDisplay,
                                                    faultType,
                                                }) => ({
                                                    path,
                                                    handle: {
                                                        breadcrumbDisplay,
                                                    },
                                                    children: [
                                                        {
                                                            index: true,
                                                            element: (
                                                                <AccessGuard
                                                                    unauthorizedBehaviour="unauthorizedPage"
                                                                    featureFlags={[
                                                                        FeatureFlag.IL_4026_UPLIFT_FAULTS_DASHBOARD,
                                                                    ]}
                                                                    accessTags={[
                                                                        "*",
                                                                    ]}
                                                                    permissions={[
                                                                        "global_dashboard",
                                                                    ]}
                                                                >
                                                                    <FaultedDeviceTable
                                                                        faultType={
                                                                            faultType
                                                                        }
                                                                    />
                                                                </AccessGuard>
                                                            ),
                                                        },
                                                        {
                                                            path: ":deviceId/:statusItemId?",
                                                            handle: {
                                                                breadcrumbKey:
                                                                    ":statusItemId",
                                                            },
                                                            element: (
                                                                <AccessGuard
                                                                    unauthorizedBehaviour="unauthorizedPage"
                                                                    featureFlags={[
                                                                        FeatureFlag.IL_4026_UPLIFT_FAULTS_DASHBOARD,
                                                                    ]}
                                                                    accessTags={[
                                                                        "*",
                                                                    ]}
                                                                    permissions={[
                                                                        "global_dashboard",
                                                                    ]}
                                                                >
                                                                    <DeviceStatusDetailPage
                                                                        faultType={
                                                                            faultType
                                                                        }
                                                                    />
                                                                </AccessGuard>
                                                            ),
                                                        },
                                                    ],
                                                }),
                                            ),
                                            {
                                                path: "warnings",
                                                handle: {
                                                    breadcrumbDisplay:
                                                        "Warnings",
                                                },
                                                children: [
                                                    {
                                                        index: true,
                                                        element: (
                                                            <AccessGuard
                                                                unauthorizedBehaviour="unauthorizedPage"
                                                                featureFlags={[
                                                                    FeatureFlag.IL_4026_UPLIFT_FAULTS_DASHBOARD,
                                                                ]}
                                                                accessTags={[
                                                                    "*",
                                                                ]}
                                                                permissions={[
                                                                    "global_dashboard",
                                                                ]}
                                                            >
                                                                <WarningsTable />
                                                            </AccessGuard>
                                                        ),
                                                    },
                                                    {
                                                        path: ":alertId",
                                                        handle: {
                                                            breadcrumbKey:
                                                                ":alertId",
                                                        },
                                                        element: (
                                                            <AccessGuard
                                                                unauthorizedBehaviour="unauthorizedPage"
                                                                featureFlags={[
                                                                    FeatureFlag.IL_4026_UPLIFT_FAULTS_DASHBOARD,
                                                                ]}
                                                                accessTags={[
                                                                    "*",
                                                                ]}
                                                                permissions={[
                                                                    "global_dashboard",
                                                                ]}
                                                            >
                                                                <WarningDetailPage />
                                                            </AccessGuard>
                                                        ),
                                                    },
                                                ],
                                            },
                                            {
                                                path: `caas/:orgId?/:tabId?`,
                                                handle: {
                                                    breadcrumbDisplay:
                                                        "CaaS dashboard",
                                                },
                                                element: (
                                                    <AccessGuard
                                                        unauthorizedBehaviour="unauthorizedPage"
                                                        accessTags={[
                                                            "JETCharge",
                                                        ]}
                                                    >
                                                        <MaintenanceDashboardPage />
                                                    </AccessGuard>
                                                ),
                                            },
                                            {
                                                path: `reports/create`,
                                                handle: {
                                                    breadcrumbDisplay:
                                                        "Create report",
                                                },
                                                element: (
                                                    <AccessGuard
                                                        unauthorizedBehaviour="unauthorizedPage"
                                                        permissions={[
                                                            "charge_transactions_read",
                                                        ]}
                                                    >
                                                        <AdhocReportsPage />
                                                    </AccessGuard>
                                                ),
                                            },
                                            // reports/scheduled route
                                            {
                                                path: "reports/:webhooksType",
                                                element: (
                                                    <AccessGuard
                                                        unauthorizedBehaviour="unauthorizedPage"
                                                        permissions={[
                                                            "webhook_create",
                                                        ]}
                                                    >
                                                        <WebhooksListPage key="network-reports-webhooks" />
                                                    </AccessGuard>
                                                ),
                                            },
                                            {
                                                path: `reports/create/:webhooksType`,
                                                handle: {
                                                    breadcrumbDisplay:
                                                        "Create report",
                                                },
                                                element: (
                                                    <AccessGuard
                                                        unauthorizedBehaviour="unauthorizedPage"
                                                        permissions={[
                                                            "charge_transactions_read",
                                                        ]}
                                                    >
                                                        <AdhocReportsPage />
                                                    </AccessGuard>
                                                ),
                                            },
                                            {
                                                path: `webhooks/:webhooksType`,
                                                handle: {
                                                    breadcrumbDisplay:
                                                        "Scheduled Reports",
                                                },
                                                element: (
                                                    <AccessGuard
                                                        unauthorizedBehaviour="unauthorizedPage"
                                                        permissions={[
                                                            "webhook_create",
                                                        ]}
                                                    >
                                                        <WebhooksListPage key="network-webhooks" />
                                                    </AccessGuard>
                                                ),
                                            },
                                            {
                                                path: "maintenance",
                                                handle: {
                                                    breadcrumbDisplay:
                                                        "Maintenance",
                                                },
                                                children:
                                                    maintenanceRoutes(flags),
                                            },
                                        ],
                                    },
                                    {
                                        path: "sites",
                                        handle: { breadcrumbDisplay: "Sites" },
                                        element: (
                                            <SidebarLayout>
                                                <SidebarStacker
                                                    key="sites"
                                                    id="sites"
                                                    ranking={0}
                                                    component={SitesSidebar}
                                                />
                                                <Outlet />
                                            </SidebarLayout>
                                        ),
                                        children: [
                                            {
                                                index: true,
                                                handle: {
                                                    breadcrumbDisplay:
                                                        "All sites",
                                                },
                                                element: (
                                                    <DeviceGroupsListPage
                                                        key={
                                                            // Key is important, to ensure fresh render when navigating from another
                                                            // DeviceGroupsListPage. Otherwise datagrid state gets mixed up.
                                                            "customers-site-list"
                                                        }
                                                        deviceGroupType="Site"
                                                    />
                                                ),
                                            },
                                            siteRoutes(flags),
                                        ],
                                    },
                                    {
                                        path: "devices",
                                        handle: {
                                            breadcrumbDisplay: "Devices",
                                        },
                                        element: (
                                            <SidebarLayout>
                                                <SidebarStacker
                                                    key="devices"
                                                    id="devices"
                                                    ranking={0}
                                                    component={DevicesSidebar}
                                                />
                                                <Outlet />
                                            </SidebarLayout>
                                        ),
                                        children: [
                                            {
                                                index: true,
                                                handle: {
                                                    breadcrumbDisplay:
                                                        "All devices",
                                                },
                                                element: (
                                                    <DevicesPage
                                                        key="devices-all-devices"
                                                        title="All devices"
                                                        filterStorageKey={
                                                            CUSTOMER_DEVICES_FILTER_STORAGE_KEY
                                                        }
                                                    />
                                                ),
                                            },
                                            deviceRoutes(
                                                flags,
                                                ":deviceIdentity",
                                            ),
                                        ],
                                    },
                                    {
                                        path: "manage-fleet",
                                        handle: {
                                            breadcrumbDisplay: "Manage fleet",
                                        },
                                        element: (
                                            <SidebarLayout>
                                                <SidebarStacker
                                                    key="manage-fleet"
                                                    id="manage-fleet"
                                                    ranking={0}
                                                    component={
                                                        ManageFleetSidebar
                                                    }
                                                />
                                                <Outlet />
                                            </SidebarLayout>
                                        ),
                                        children: [
                                            {
                                                index: true,
                                                element: (
                                                    <Navigate
                                                        to={"vehicles"}
                                                        replace
                                                    />
                                                ),
                                            },
                                            {
                                                path: "vehicles",
                                                handle: {
                                                    breadcrumbDisplay:
                                                        "Vehicles",
                                                },
                                                children: [
                                                    {
                                                        index: true,
                                                        element: (
                                                            <VehiclesPage
                                                                key="vehicles-list"
                                                                title="All vehicles"
                                                                filterStorageKey="customer-vehicles"
                                                            />
                                                        ),
                                                    },
                                                    {
                                                        path: ":vehicleIdentity",
                                                        handle: {
                                                            breadcrumbKey:
                                                                ":vehicleIdentity",
                                                        },
                                                        element: (
                                                            <VehiclePage />
                                                        ),
                                                    },
                                                ],
                                            },
                                        ],
                                    },
                                    {
                                        path: "search",
                                        handle: { breadcrumbDisplay: "Search" },
                                        element: (
                                            <SidebarLayout>
                                                <SidebarStacker
                                                    key="search"
                                                    id="search"
                                                    ranking={0}
                                                    component={SearchSidebar}
                                                />
                                                <Outlet />
                                            </SidebarLayout>
                                        ),
                                        children: [
                                            {
                                                index: true,
                                                element: (
                                                    <Navigate
                                                        to={"devices"}
                                                        replace
                                                    />
                                                ),
                                            },

                                            {
                                                path: "devices/:tabId?",
                                                handle: {
                                                    breadcrumbDisplay:
                                                        "Devices",
                                                },
                                                element: (
                                                    <DevicesPage
                                                        key="search-devices"
                                                        title="Search devices"
                                                        filterStorageKey="search-devices"
                                                    />
                                                ),
                                            },
                                            {
                                                path: "sessions/*",
                                                handle: {
                                                    breadcrumbDisplay:
                                                        "Sessions",
                                                },
                                                element: (
                                                    <AccessGuard
                                                        unauthorizedBehaviour="unauthorizedPage"
                                                        permissions={[
                                                            "charge_transactions_read",
                                                        ]}
                                                    >
                                                        <TransactionSearchPage />
                                                    </AccessGuard>
                                                ),
                                            },
                                        ],
                                    },
                                    {
                                        path: "settings",
                                        handle: {
                                            breadcrumbDisplay: "Settings",
                                        },
                                        element: (
                                            <SidebarLayout>
                                                <SidebarStacker
                                                    key="settings"
                                                    id="settings"
                                                    ranking={0}
                                                    component={SettingsSidebar}
                                                />
                                                <Outlet />
                                            </SidebarLayout>
                                        ),
                                        children: [
                                            {
                                                index: true,
                                                element: (
                                                    <Navigate
                                                        to={"account"}
                                                        replace
                                                    />
                                                ),
                                            },
                                            {
                                                path: "account",
                                                handle: {
                                                    breadcrumbDisplay:
                                                        "My account",
                                                },
                                                element: <AccountInfoPage />,
                                            },
                                            {
                                                path: "notification-preferences",
                                                handle: {
                                                    breadcrumbDisplay:
                                                        "Notification preferences",
                                                },
                                                children: [
                                                    {
                                                        index: true,
                                                        element: (
                                                            <AccessGuard
                                                                unauthorizedBehaviour="unauthorizedPage"
                                                                permissions={[
                                                                    "notification_preferences_read",
                                                                ]}
                                                            >
                                                                <NotificationPreferencePage />
                                                            </AccessGuard>
                                                        ),
                                                    },
                                                    {
                                                        path: "create",
                                                        handle: {
                                                            breadcrumbDisplay:
                                                                "Create preference",
                                                        },
                                                        element: (
                                                            <AccessGuard
                                                                unauthorizedBehaviour="unauthorizedPage"
                                                                permissions={[
                                                                    "notification_preferences_manage",
                                                                ]}
                                                            >
                                                                <CrudNotificationPreferencePage />
                                                            </AccessGuard>
                                                        ),
                                                    },
                                                    {
                                                        path: ":notificationPreferenceId",
                                                        handle: {
                                                            breadcrumbDisplay:
                                                                "Update preference",
                                                        },
                                                        element: (
                                                            <AccessGuard
                                                                unauthorizedBehaviour="unauthorizedPage"
                                                                permissions={[
                                                                    "notification_preferences_manage",
                                                                ]}
                                                            >
                                                                <CrudNotificationPreferencePage />
                                                            </AccessGuard>
                                                        ),
                                                    },
                                                ],
                                            },
                                            {
                                                path: "organisation",
                                                handle: {
                                                    breadcrumbDisplay:
                                                        "Organisation",
                                                },
                                                element: (
                                                    <OrganisationPreferencesPage />
                                                ),
                                            },
                                            {
                                                path: "users",
                                                handle: {
                                                    breadcrumbDisplay: "Users",
                                                },
                                                element: (
                                                    <AccessGuard
                                                        unauthorizedBehaviour="unauthorizedPage"
                                                        permissions={[
                                                            "system_user_account_manage",
                                                            "enterprise_user_account_read",
                                                        ]}
                                                        permissionsSelector="any"
                                                    >
                                                        <UsersListPage />
                                                    </AccessGuard>
                                                ),
                                            },
                                            {
                                                path: "roles",
                                                handle: {
                                                    breadcrumbDisplay: "Roles",
                                                },
                                                element: (
                                                    <AccessGuard
                                                        unauthorizedBehaviour="unauthorizedPage"
                                                        accessTags={[
                                                            "JETCharge",
                                                        ]}
                                                    >
                                                        <RolesList />
                                                    </AccessGuard>
                                                ),
                                            },
                                            {
                                                path: "user-action-log",
                                                handle: {
                                                    breadcrumbDisplay:
                                                        "User action log",
                                                },
                                                element: (
                                                    <AccessGuard
                                                        unauthorizedBehaviour="unauthorizedPage"
                                                        permissions={[
                                                            "user_tasks_read",
                                                        ]}
                                                    >
                                                        <UserActionLogList />
                                                    </AccessGuard>
                                                ),
                                            },
                                            {
                                                path: "payment-log",
                                                handle: {
                                                    breadcrumbDisplay:
                                                        "Payment log",
                                                },
                                                element: (
                                                    <AccessGuard
                                                        unauthorizedBehaviour="unauthorizedPage"
                                                        permissions={[
                                                            "system_device_provisioning",
                                                        ]}
                                                        featureFlags={[
                                                            FeatureFlag.PAYMENT_LOGS_FEATURE_FLAG,
                                                        ]}
                                                    >
                                                        <PaymentLogList />
                                                    </AccessGuard>
                                                ),
                                            },
                                            {
                                                path: "rfid",
                                                handle: {
                                                    breadcrumbDisplay:
                                                        "RFID tags",
                                                },
                                                children: [
                                                    {
                                                        index: true,
                                                        element: (
                                                            <AccessGuard
                                                                unauthorizedBehaviour="unauthorizedPage"
                                                                permissions={[
                                                                    "rfid_tags_read",
                                                                    "rfid_tags_manage",
                                                                    "system_rfid_tags_manage",
                                                                ]}
                                                                permissionsSelector="any"
                                                                featureFlags={[
                                                                    FeatureFlag.IL_4077_TANSTACK_ODATA_TABLES,
                                                                ]}
                                                                flagOffElement={
                                                                    <AccessGuard
                                                                        unauthorizedBehaviour="unauthorizedPage"
                                                                        permissions={[
                                                                            "rfid_tags_read",
                                                                            "rfid_tags_manage",
                                                                            "system_rfid_tags_manage",
                                                                        ]}
                                                                        permissionsSelector="any"
                                                                        featureFlags={[
                                                                            FeatureFlag.IL_3977_RFID_UPLIFT,
                                                                        ]}
                                                                        flagOffElement={
                                                                            <RfidTagListPageDeprecated />
                                                                        }
                                                                    >
                                                                        <RfidTagListPage />
                                                                    </AccessGuard>
                                                                }
                                                            >
                                                                <RfidTagTablePage />
                                                            </AccessGuard>
                                                        ),
                                                    },
                                                    {
                                                        path: ":rfidTagId",
                                                        handle: {
                                                            breadcrumbKey:
                                                                ":rfidTagId",
                                                        },
                                                        element: (
                                                            <AccessGuard
                                                                unauthorizedBehaviour="unauthorizedPage"
                                                                permissions={[
                                                                    "rfid_tags_read",
                                                                    "rfid_tags_manage",
                                                                    "system_rfid_tags_manage",
                                                                ]}
                                                                permissionsSelector="any"
                                                            >
                                                                <RfidTagDetailPage />
                                                            </AccessGuard>
                                                        ),
                                                    },
                                                ],
                                            },
                                            {
                                                path: "offers",
                                                handle: {
                                                    breadcrumbDisplay: "Offers",
                                                },
                                                children: [
                                                    {
                                                        index: true,
                                                        element: (
                                                            <AccessGuard
                                                                unauthorizedBehaviour="unauthorizedPage"
                                                                permissions={[
                                                                    "device_pricing_read",
                                                                ]}
                                                                featureFlags={[
                                                                    FeatureFlag.EM_25_OFFERS,
                                                                ]}
                                                            >
                                                                <OfferTablePage />
                                                            </AccessGuard>
                                                        ),
                                                    },
                                                ],
                                            },
                                            {
                                                path: "tariffs",
                                                handle: {
                                                    breadcrumbDisplay:
                                                        "Tariffs",
                                                },
                                                children: [
                                                    {
                                                        index: true,
                                                        element: (
                                                            <AccessGuard
                                                                unauthorizedBehaviour="unauthorizedPage"
                                                                accessTags={[
                                                                    "*",
                                                                ]}
                                                                permissions={[
                                                                    "device_pricing_read",
                                                                    "device_pricing_manage",
                                                                ]}
                                                                permissionsSelector="any"
                                                                featureFlags={[
                                                                    FeatureFlag.TARIFF_FEATURE_FLAG,
                                                                ]}
                                                            >
                                                                <TariffPage />
                                                            </AccessGuard>
                                                        ),
                                                    },
                                                    {
                                                        path: ":pricingScheduleId/:pricingScheduleGroupId",
                                                        element: (
                                                            <AccessGuard
                                                                unauthorizedBehaviour="unauthorizedPage"
                                                                accessTags={[
                                                                    "*",
                                                                ]}
                                                                permissions={[
                                                                    "device_pricing_read",
                                                                    "device_pricing_manage",
                                                                ]}
                                                                permissionsSelector="any"
                                                                featureFlags={[
                                                                    FeatureFlag.TARIFF_FEATURE_FLAG,
                                                                ]}
                                                            >
                                                                <TariffDetailsPage />
                                                            </AccessGuard>
                                                        ),
                                                    },
                                                    {
                                                        path: "create",
                                                        element: (
                                                            <AccessGuard
                                                                unauthorizedBehaviour="unauthorizedPage"
                                                                accessTags={[
                                                                    "*",
                                                                ]}
                                                                permissions={[
                                                                    "device_pricing_read",
                                                                    "device_pricing_manage",
                                                                ]}
                                                                permissionsSelector="any"
                                                                featureFlags={[
                                                                    FeatureFlag.TARIFF_FEATURE_FLAG,
                                                                ]}
                                                            >
                                                                <CreateTariffPage />
                                                            </AccessGuard>
                                                        ),
                                                    },
                                                    {
                                                        path: "edit/:pricingScheduleId/:pricingScheduleGroupId",
                                                        element: (
                                                            <AccessGuard
                                                                unauthorizedBehaviour="unauthorizedPage"
                                                                accessTags={[
                                                                    "*",
                                                                ]}
                                                                permissions={[
                                                                    "device_pricing_read",
                                                                    "device_pricing_manage",
                                                                ]}
                                                                permissionsSelector="any"
                                                                featureFlags={[
                                                                    FeatureFlag.TARIFF_FEATURE_FLAG,
                                                                ]}
                                                            >
                                                                <EditTariffPage />
                                                            </AccessGuard>
                                                        ),
                                                    },
                                                ],
                                            },
                                            {
                                                path: "device-groups",
                                                handle: {
                                                    breadcrumbDisplay:
                                                        "Device groups",
                                                },
                                                element: (
                                                    <AccessGuard
                                                        unauthorizedBehaviour="unauthorizedPage"
                                                        accessTags={["*"]}
                                                    >
                                                        <Outlet />
                                                    </AccessGuard>
                                                ),
                                                children: [
                                                    {
                                                        index: true,
                                                        element: (
                                                            <DeviceGroupsListPage
                                                                key={
                                                                    // Key is important, to ensure fresh render when navigating from another
                                                                    // DeviceGroupsListPage. Otherwise datagrid state gets mixed up.
                                                                    "settings-device-group"
                                                                }
                                                            />
                                                        ),
                                                    },
                                                    deviceGroupRoutes(flags),
                                                ],
                                            },
                                            {
                                                path: "device-catalogue",
                                                handle: {
                                                    breadcrumbDisplay:
                                                        "Device catalogue",
                                                },
                                                element: (
                                                    <AccessGuard
                                                        unauthorizedBehaviour="unauthorizedPage"
                                                        permissions={[
                                                            "system_device_catalogue_update",
                                                        ]}
                                                        featureFlags={[
                                                            FeatureFlag.IL_3915_DEVICE_CATALOGUE,
                                                        ]}
                                                    >
                                                        <DeviceCatalogueTablePage />
                                                    </AccessGuard>
                                                ),
                                            },
                                        ],
                                    },
                                    {
                                        path: "manage-customers",
                                        handle: {
                                            breadcrumbDisplay:
                                                "Manage customers",
                                        },
                                        element: (
                                            <SidebarLayout>
                                                <SidebarStacker
                                                    key="manage-customers"
                                                    id="manage-customers"
                                                    ranking={0}
                                                    component={
                                                        ManageCustomersSidebar
                                                    }
                                                />
                                                <Outlet />
                                            </SidebarLayout>
                                        ),
                                        children: [
                                            {
                                                index: true,
                                                element: (
                                                    <Navigate
                                                        to={"organisations"}
                                                        replace
                                                    />
                                                ),
                                            },
                                            {
                                                path: "organisations",
                                                handle: {
                                                    breadcrumbDisplay:
                                                        "Organisations",
                                                },
                                                element: (
                                                    <AccessGuard
                                                        unauthorizedBehaviour="unauthorizedPage"
                                                        permissions={[
                                                            "device_group_create",
                                                            "partner_organisation_manage",
                                                        ]}
                                                        permissionsSelector="any"
                                                    >
                                                        <Outlet />
                                                    </AccessGuard>
                                                ),
                                                children: [
                                                    {
                                                        index: true,
                                                        element: (
                                                            <DeviceGroupsListPage
                                                                key="organisations-list"
                                                                deviceGroupType="Organisation"
                                                            />
                                                        ),
                                                    },
                                                    {
                                                        handle: {
                                                            breadcrumbKey:
                                                                ":deviceGroupTag",
                                                        },
                                                        path: ":deviceGroupTag",
                                                        element: (
                                                            <>
                                                                <DeviceGroupBreadcrumbSetter />
                                                                <SidebarStacker
                                                                    key="organisations"
                                                                    id="organisations"
                                                                    ranking={1}
                                                                    component={
                                                                        OrganisationSidebar
                                                                    }
                                                                />
                                                                <Outlet />
                                                            </>
                                                        ),
                                                        children: [
                                                            {
                                                                index: true,
                                                                element: (
                                                                    <Navigate
                                                                        to={
                                                                            "overview"
                                                                        }
                                                                        replace
                                                                    />
                                                                ),
                                                            },
                                                            {
                                                                path: "overview",
                                                                handle: {
                                                                    breadcrumbDisplay:
                                                                        "Overview",
                                                                },
                                                                element: (
                                                                    <DeviceGroupPage />
                                                                ),
                                                            },
                                                            {
                                                                path: "devices",
                                                                handle: {
                                                                    breadcrumbDisplay:
                                                                        "Devices",
                                                                },
                                                                element: (
                                                                    <DeviceListForDeviceGroup
                                                                        asPage
                                                                    />
                                                                ),
                                                            },
                                                        ],
                                                    },
                                                ],
                                            },
                                            {
                                                path: "sites",
                                                handle: {
                                                    breadcrumbDisplay: "Sites",
                                                },

                                                children: [
                                                    {
                                                        index: true,
                                                        element: (
                                                            <AccessGuard
                                                                unauthorizedBehaviour="unauthorizedPage"
                                                                accessTags={[
                                                                    "*",
                                                                ]}
                                                            >
                                                                <DeviceGroupsListPage
                                                                    key={
                                                                        // Key is important, to ensure fresh render when navigating from another
                                                                        // DeviceGroupsListPage. Otherwise datagrid state gets mixed up.
                                                                        "site-list"
                                                                    }
                                                                    deviceGroupType="Site"
                                                                />
                                                            </AccessGuard>
                                                        ),
                                                    },
                                                    siteRoutes(flags),
                                                ],
                                            },
                                            {
                                                path: "device-groups",
                                                handle: {
                                                    breadcrumbDisplay:
                                                        "Device groups",
                                                },
                                                children: [
                                                    {
                                                        index: true,
                                                        element: (
                                                            <AccessGuard
                                                                unauthorizedBehaviour="unauthorizedPage"
                                                                accessTags={[
                                                                    "*",
                                                                ]}
                                                            >
                                                                <DeviceGroupsListPage
                                                                    key={
                                                                        // Key is important, to ensure fresh render when navigating from another
                                                                        // DeviceGroupsListPage. Otherwise datagrid state gets mixed up.
                                                                        "all-device-groups-list"
                                                                    }
                                                                />
                                                            </AccessGuard>
                                                        ),
                                                    },
                                                    deviceGroupRoutes(flags),
                                                ],
                                            },
                                            {
                                                path: "devices",
                                                handle: {
                                                    breadcrumbDisplay:
                                                        "Devices",
                                                },
                                                children: [
                                                    {
                                                        index: true,
                                                        element: (
                                                            <AccessGuard
                                                                unauthorizedBehaviour="unauthorizedPage"
                                                                accessTags={[
                                                                    "*",
                                                                ]}
                                                            >
                                                                <DevicesPage
                                                                    key="manage-customers-devices"
                                                                    title="Devices"
                                                                    filterStorageKey={
                                                                        MANAGE_CUSTOMERS_DEVICES_FILTER_STORAGE_KEY
                                                                    }
                                                                />
                                                            </AccessGuard>
                                                        ),
                                                    },
                                                    deviceRoutes(
                                                        flags,
                                                        ":deviceIdentity",
                                                    ),
                                                ],
                                            },
                                            {
                                                path: "device-owners",
                                                handle: {
                                                    breadcrumbDisplay:
                                                        "Device owners",
                                                },
                                                children: [
                                                    {
                                                        index: true,
                                                        element: (
                                                            <AccessGuard
                                                                unauthorizedBehaviour="unauthorizedPage"
                                                                accessTags={[
                                                                    "*",
                                                                ]}
                                                                permissions={[
                                                                    "system_device_provisioning",
                                                                ]}
                                                            >
                                                                <DeviceOwnersPage />
                                                            </AccessGuard>
                                                        ),
                                                    },
                                                    {
                                                        path: ":deviceOwnerId",
                                                        handle: {
                                                            breadcrumbKey:
                                                                ":deviceOwnerId",
                                                        },
                                                        element: (
                                                            <AccessGuard
                                                                unauthorizedBehaviour="unauthorizedPage"
                                                                accessTags={[
                                                                    "*",
                                                                ]}
                                                                permissions={[
                                                                    "system_device_provisioning",
                                                                ]}
                                                            >
                                                                {/* Note the not-plural here! (vs DeviceOwnersPage) */}
                                                                <DeviceOwnerPage />
                                                            </AccessGuard>
                                                        ),
                                                    },
                                                ],
                                            },

                                            {
                                                path: "add-device-owner/:deviceOwnerId",
                                                handle: {
                                                    breadcrumbDisplay:
                                                        "Device owners",
                                                },
                                                element: (
                                                    <AccessGuard
                                                        unauthorizedBehaviour="unauthorizedPage"
                                                        accessTags={[
                                                            "JETCharge",
                                                        ]}
                                                        permissions={[
                                                            "system_device_provisioning",
                                                        ]}
                                                    >
                                                        {/* Note the not-plural here! (vs DeviceOwnersPage) */}
                                                        <DeviceOwnerPage />
                                                    </AccessGuard>
                                                ),
                                            },
                                            {
                                                path: "users",
                                                handle: {
                                                    breadcrumbDisplay: "Users",
                                                },
                                                element: (
                                                    <AccessGuard
                                                        unauthorizedBehaviour="unauthorizedPage"
                                                        permissions={[
                                                            "system_user_account_manage",
                                                            "enterprise_user_account_read",
                                                        ]}
                                                        permissionsSelector="any"
                                                    >
                                                        <UsersListPage />
                                                    </AccessGuard>
                                                ),
                                            },
                                            {
                                                path: "maintenance",
                                                children:
                                                    maintenanceRoutes(flags),
                                            },
                                        ],
                                    },
                                    {
                                        path: "system",
                                        handle: { breadcrumbDisplay: "System" },
                                        element: (
                                            <SidebarLayout>
                                                <SidebarStacker
                                                    key="system"
                                                    id="system"
                                                    ranking={0}
                                                    component={SystemSidebar}
                                                />
                                                <Outlet />
                                            </SidebarLayout>
                                        ),
                                        children: [
                                            {
                                                index: true,
                                                element: (
                                                    <Navigate
                                                        to="central-system"
                                                        replace
                                                    />
                                                ),
                                            },
                                            {
                                                path: "central-system",
                                                handle: {
                                                    breadcrumbDisplay:
                                                        "Central system",
                                                },
                                                element: (
                                                    <AccessGuard
                                                        unauthorizedBehaviour="unauthorizedPage"
                                                        accessTags={[
                                                            "IlluminateAdmin",
                                                        ]}
                                                    >
                                                        <CentralSystemsListPage />
                                                    </AccessGuard>
                                                ),
                                            },
                                            {
                                                path: "webhooks/:webhooksType",
                                                handle: {
                                                    breadcrumbDisplay:
                                                        "Webhooks",
                                                },
                                                element: (
                                                    <AccessGuard
                                                        unauthorizedBehaviour="unauthorizedPage"
                                                        permissions={[
                                                            "webhook_create",
                                                        ]}
                                                    >
                                                        <WebhooksListPage key="system-webhooks" />
                                                    </AccessGuard>
                                                ),
                                            },
                                            {
                                                path: "passthroughs",
                                                handle: {
                                                    breadcrumbDisplay:
                                                        "Passthroughs",
                                                },
                                                element: (
                                                    <AccessGuard
                                                        unauthorizedBehaviour="unauthorizedPage"
                                                        accessTags={[
                                                            "IlluminateAdmin",
                                                        ]}
                                                        permissions={[
                                                            "system_passthrough_manage",
                                                            "system_device_provisioning",
                                                        ]}
                                                        permissionsSelector="any"
                                                    >
                                                        <PassthroughConfigsListPage />
                                                    </AccessGuard>
                                                ),
                                            },
                                            {
                                                path: "device-schedule-templates",
                                                handle: {
                                                    breadcrumbDisplay:
                                                        "Device schedule templates",
                                                },
                                                element: (
                                                    <AccessGuard
                                                        unauthorizedBehaviour="unauthorizedPage"
                                                        accessTags={[
                                                            "IlluminateAdmin",
                                                        ]}
                                                    >
                                                        <DeviceScheduleTemplatesList />
                                                    </AccessGuard>
                                                ),
                                            },
                                            {
                                                path: "firmware",
                                                handle: {
                                                    breadcrumbDisplay:
                                                        "Firmware",
                                                },
                                                element: (
                                                    <AccessGuard
                                                        unauthorizedBehaviour="unauthorizedPage"
                                                        accessTags={[
                                                            "IlluminateAdmin",
                                                        ]}
                                                    >
                                                        <FirmwareLibraryPage />
                                                    </AccessGuard>
                                                ),
                                            },
                                            {
                                                path: "service-models",
                                                handle: {
                                                    breadcrumbDisplay:
                                                        "Service models",
                                                },
                                                children: [
                                                    {
                                                        index: true,
                                                        element: (
                                                            <AccessGuard
                                                                unauthorizedBehaviour="unauthorizedPage"
                                                                featureFlags={[
                                                                    FeatureFlag.IL_3133_SERVICE_MODEL,
                                                                ]}
                                                                accessTags={[
                                                                    "IlluminateAdmin",
                                                                ]}
                                                            >
                                                                <ServiceModelsPage />
                                                            </AccessGuard>
                                                        ),
                                                    },
                                                    deviceGroupRoutes(flags),
                                                ],
                                            },
                                            {
                                                path: "vehicle-model-catalog",
                                                handle: {
                                                    breadcrumbDisplay:
                                                        "Vehicle model catalog",
                                                },
                                                element: (
                                                    <AccessGuard
                                                        unauthorizedBehaviour="unauthorizedPage"
                                                        permissions={[
                                                            IlluminateUserPermissionsEnum.SystemVehicleModelManage,
                                                        ]}
                                                        featureFlags={[
                                                            FeatureFlag.IL_4063_VEHICLE_MODEL_CATALOG,
                                                        ]}
                                                    >
                                                        <VehicleModelCatalogPage />
                                                    </AccessGuard>
                                                ),
                                            },
                                        ],
                                    },

                                    {
                                        path: "preview-role",
                                        element: (
                                            <AccessGuard
                                                unauthorizedBehaviour="unauthorizedPage"
                                                accessTags={["JETCharge"]}
                                            >
                                                <PreviewRoleRedirect />
                                            </AccessGuard>
                                        ),
                                    },
                                ],
                            },
                        ],
                    },
                ],
            },
        ]),
    );

const siteRoutes = (flags: LDFlagSet): RouteObject => {
    return {
        path: ":deviceGroupTag",
        handle: {
            breadcrumbKey: ":deviceGroupTag",
        },
        element: (
            <>
                <DeviceGroupBreadcrumbSetter />
                <SidebarStacker
                    id="site"
                    key="site"
                    component={DeviceGroupSidebar}
                    ranking={1}
                />
                <Outlet />
            </>
        ),
        children: [
            {
                index: true,
                element: <Navigate to={"overview"} replace />,
            },
            {
                children: [
                    {
                        path: "overview",
                        element: (
                            <AccessGuard
                                unauthorizedBehaviour="unauthorizedPage"
                                permissions={["global_dashboard"]}
                            >
                                <DeviceGroupPage />
                            </AccessGuard>
                        ),
                    },
                    {
                        path: "devices",
                        element: <DeviceListForDeviceGroup asPage />,
                    },
                ],
            },

            deviceRoutes(flags, "devices/:deviceIdentity"),
        ],
    };
};

const maintenanceRoutes = (flags: LDFlagSet): RouteObject[] => {
    return [
        {
            index: true,
            element: (
                <>
                    <AccessGuard
                        permissions={["ticketing_read"]}
                        unauthorizedBehaviour="hide"
                    >
                        <Navigate to="tickets" replace />
                    </AccessGuard>
                    <AccessGuard
                        featureFlags={[
                            FeatureFlag.IL_3705_PREVENTATIVE_PLANNED_MAINTENANCE,
                        ]}
                        permissions={[IlluminateUserPermissionsEnum.PpmRead]}
                        permissionsSelector="all"
                        unauthorizedBehaviour="hide"
                    >
                        <Navigate to="service-events" replace />
                    </AccessGuard>
                </>
            ),
        },
        {
            path: "tickets",
            handle: {
                breadcrumbDisplay: "Tickets",
            },
            element: (
                <AccessGuard
                    unauthorizedBehaviour="unauthorizedPage"
                    permissions={["ticketing_read"]}
                >
                    <MaintenanceTicketsPage />,
                </AccessGuard>
            ),
        },
        {
            path: "service-events",
            handle: {
                breadcrumbDisplay: "Service events",
            },
            element: (
                <AccessGuard
                    featureFlags={[
                        FeatureFlag.IL_3705_PREVENTATIVE_PLANNED_MAINTENANCE,
                    ]}
                    permissions={[IlluminateUserPermissionsEnum.PpmRead]}
                    permissionsSelector="all"
                    unauthorizedBehaviour="unauthorizedPage"
                >
                    <ServiceEventList asPage />
                </AccessGuard>
            ),
        },
        {
            path: "schedules",
            handle: {
                breadcrumbDisplay: "Schedules",
            },
            children: [
                {
                    index: true,
                    element: (
                        <AccessGuard
                            featureFlags={[
                                FeatureFlag.IL_3705_PREVENTATIVE_PLANNED_MAINTENANCE,
                            ]}
                            permissions={[
                                IlluminateUserPermissionsEnum.PpmRead,
                            ]}
                            permissionsSelector="all"
                            unauthorizedBehaviour="unauthorizedPage"
                        >
                            <DeviceGroupsListPage deviceGroupType="ScheduledMaintenance" />
                        </AccessGuard>
                    ),
                },
                deviceGroupRoutes(flags),
            ],
        },
    ];
};

const deviceGroupRoutes = (flags: LDFlagSet): RouteObject => {
    return {
        path: ":deviceGroupTag",
        handle: {
            breadcrumbKey: ":deviceGroupTag",
        },
        element: (
            <>
                <DeviceGroupBreadcrumbSetter />
                <SidebarStacker
                    id="device-group"
                    key="device-group"
                    component={DeviceGroupSidebar}
                    ranking={1}
                />
                <Outlet />
            </>
        ),
        children: [
            {
                index: true,
                element: <Navigate to={"overview"} replace />,
            },
            {
                children: [
                    {
                        path: "overview",
                        element: (
                            <AccessGuard
                                unauthorizedBehaviour="unauthorizedPage"
                                permissions={["global_dashboard"]}
                            >
                                <DeviceGroupPage />
                            </AccessGuard>
                        ),
                    },
                    {
                        path: "devices",
                        element: <DeviceListForDeviceGroup asPage />,
                    },
                    deviceRoutes(flags, "devices/:deviceIdentity", false),
                ],
            },
        ],
    };
};

// Legacy back-compat content for `site-configuration`/`device-configuration`
// (pre-Load-Management device-config URLs). These routes are always
// registered (see IL-4187) — each reads useFlags() itself at render time so
// the redirect-vs-legacy-content decision is never made from a flags snapshot
// captured before LaunchDarkly's identify() has resolved the real user.
const LegacySiteConfigurationIndexContent = () => {
    const flags = useFlags();
    if (flags[FeatureFlag.IL_4187_LOAD_MANAGEMENT]) {
        return <Navigate to="../core-configuration" relative="path" replace />;
    }
    return (
        <AccessGuard
            unauthorizedBehaviour="unauthorizedPage"
            permissions={[
                "system_device_provisioning",
                "partner_device_provisioning",
            ]}
            permissionsSelector="any"
        >
            <CoreConfigPageDeprecated />
        </AccessGuard>
    );
};

const LegacyDeviceConfigurationRedirect = () => {
    const flags = useFlags();
    return flags[FeatureFlag.IL_4187_LOAD_MANAGEMENT] ? (
        <Navigate to="../core-configuration" replace />
    ) : (
        <Navigate to=".." relative="route" replace />
    );
};

const deviceRoutes = (
    flags: LDFlagSet,
    path: string,
    enforceSiteTag = true,
): RouteObject => {
    return {
        path,
        handle: {
            breadcrumbKey: ":deviceIdentity",
        },
        element: (
            <SiteRouteFixer enforceSiteTag={enforceSiteTag}>
                <DebugConsole />

                <Outlet />
            </SiteRouteFixer>
        ),
        children: [
            {
                index: true,
                element: <DevicePage />,
            },
            // Always registered — see IL-4187. A hit on any of these three
            // paths must never fall through to the trailing wildcard route
            // just because `flags` (captured at router-construction time)
            // hasn't caught up with LaunchDarkly's identify() yet.
            {
                path: "core-configuration",
                handle: {
                    breadcrumbDisplay: "Configuration",
                },
                children: [
                    {
                        index: true,
                        element: (
                            <AccessGuard
                                unauthorizedBehaviour="unauthorizedPage"
                                permissions={[
                                    "system_device_provisioning",
                                    "partner_device_provisioning",
                                ]}
                                permissionsSelector="any"
                            >
                                {flags[FeatureFlag.IL_4204_CONFIG_SITE_TREE] ? (
                                    <ConfigTreePage />
                                ) : (
                                    <CoreConfigurationPage />
                                )}
                            </AccessGuard>
                        ),
                    },
                    {
                        path: "changelog",
                        handle: {
                            breadcrumbDisplay: "Changelog",
                        },
                        children: [
                            {
                                index: true,
                                element: (
                                    <AccessGuard
                                        unauthorizedBehaviour="unauthorizedPage"
                                        featureFlags={[
                                            FeatureFlag.COREV2_CORE_CONFIG_CHANGELOG,
                                        ]}
                                        permissions={[
                                            "system_device_provisioning",
                                            "partner_device_provisioning",
                                        ]}
                                        permissionsSelector="any"
                                    >
                                        <CoreConfigChangelogPage />
                                    </AccessGuard>
                                ),
                            },
                            {
                                path: ":coreConfigId",
                                handle: {
                                    breadCrumbUseParam: true,
                                },
                                element: (
                                    <AccessGuard
                                        unauthorizedBehaviour="unauthorizedPage"
                                        featureFlags={[
                                            FeatureFlag.COREV2_CORE_CONFIG_CHANGELOG,
                                        ]}
                                        permissions={[
                                            "system_device_provisioning",
                                            "partner_device_provisioning",
                                        ]}
                                        permissionsSelector="any"
                                    >
                                        {flags[
                                            FeatureFlag.IL_4204_CONFIG_SITE_TREE
                                        ] ? (
                                            <ConfigTreePage />
                                        ) : (
                                            <CoreConfigPreviewPage />
                                        )}
                                    </AccessGuard>
                                ),
                            },
                        ],
                    },
                ],
            },
            // Legacy device-scoped core-config URLs. Content/redirect choice
            // is decided live inside each element via its own useFlags() call
            // — see LegacySiteConfigurationIndexContent /
            // LegacyDeviceConfigurationRedirect above.
            {
                path: "site-configuration",
                handle: {
                    breadcrumbDisplay: "Site configuration",
                },
                children: [
                    {
                        index: true,
                        element: <LegacySiteConfigurationIndexContent />,
                    },
                    {
                        path: "changelog",
                        handle: {
                            breadcrumbDisplay: "Changelog",
                        },
                        element: (
                            <AccessGuard
                                unauthorizedBehaviour="unauthorizedPage"
                                featureFlags={[
                                    FeatureFlag.COREV2_CORE_CONFIG_CHANGELOG,
                                ]}
                                permissions={[
                                    "system_device_provisioning",
                                    "partner_device_provisioning",
                                ]}
                                permissionsSelector="any"
                            >
                                <CoreConfigChangelogPage />
                            </AccessGuard>
                        ),
                    },
                    {
                        path: ":coreConfigId",
                        handle: {
                            breadCrumbUseParam: true,
                        },
                        element: (
                            <AccessGuard
                                unauthorizedBehaviour="unauthorizedPage"
                                featureFlags={[
                                    FeatureFlag.COREV2_CORE_CONFIG_CHANGELOG,
                                ]}
                                permissions={[
                                    "system_device_provisioning",
                                    "partner_device_provisioning",
                                ]}
                                permissionsSelector="any"
                            >
                                <CoreConfigPreviewPage />
                            </AccessGuard>
                        ),
                    },
                ],
            },
            {
                path: "device-configuration",
                element: <LegacyDeviceConfigurationRedirect />,
            },
            {
                path: "core-charger-logs",
                handle: {
                    breadcrumbDisplay: "Core charger logs",
                },
                element: (
                    <AccessGuard
                        unauthorizedBehaviour="unauthorizedPage"
                        featureFlags={[
                            FeatureFlag.CORE_EXPORT_CHARGER_LOGS_FLAG,
                        ]}
                        permissions={["device_logs_read"]}
                        permissionsSelector="any"
                    >
                        <CoreChargerLogsPage />
                    </AccessGuard>
                ),
            },
            {
                path: "site-diagram",
                handle: {
                    breadcrumbDisplay: "Site diagram",
                },
                element: (
                    <AccessGuard
                        unauthorizedBehaviour="unauthorizedPage"
                        featureFlags={[FeatureFlag.COREV2_SITE_DIAGRAM_FLAG]}
                        permissions={[
                            "system_device_provisioning",
                            "partner_device_provisioning",
                        ]}
                        permissionsSelector="any"
                    >
                        <SiteDiagramPage />
                    </AccessGuard>
                ),
            },
            {
                path: "asat",
                handle: {
                    breadcrumbDisplay: "ASAT",
                },
                element: (
                    <AccessGuard
                        unauthorizedBehaviour="unauthorizedPage"
                        featureFlags={[FeatureFlag.IL_4181_ASAT]}
                        permissions={[
                            "system_device_provisioning",
                            "partner_device_provisioning",
                        ]}
                        permissionsSelector="any"
                    >
                        <AsatTreePage />
                    </AccessGuard>
                ),
            },
            {
                path: "configuration",
                handle: {
                    breadcrumbDisplay: "Configuration",
                },
                element: (
                    <AccessGuard
                        unauthorizedBehaviour="unauthorizedPage"
                        permissions={[
                            IlluminateUserPermissionsEnum.DevicePassthroughManage,
                            IlluminateUserPermissionsEnum.DeviceConfigRead,
                        ]}
                    >
                        <DeviceConfigurationPage />
                    </AccessGuard>
                ),
            },
            {
                path: "sessions",
                handle: {
                    breadcrumbDisplay: "Sessions",
                },
                children: [
                    {
                        index: true,
                        element: <TransactionListPage />,
                    },
                    {
                        path: ":transactionId",
                        handle: {
                            breadcrumbKey: ":transactionId",
                        },
                        element: <TransactionPage />,
                    },
                ],
            },
            {
                path: "message-log",
                handle: {
                    breadcrumbDisplay: "Message log",
                },
                element: (
                    <AccessGuard
                        unauthorizedBehaviour="unauthorizedPage"
                        permissions={[
                            IlluminateUserPermissionsEnum.DeviceLogsRead,
                        ]}
                    >
                        <DeviceMessageHistory />
                    </AccessGuard>
                ),
            },
            {
                path: "status-history",
                handle: {
                    breadcrumbDisplay: "Status history",
                },
                element: <DeviceStatusHistory />,
            },
            {
                path: "connectivity",
                handle: {
                    breadcrumbDisplay: "Connectivity",
                },
                element: <ConnectivityHistoryPage />,
            },
            {
                path: "*",
                element: <Navigate to=".." relative="route" replace />,
            },
        ],
    };
};

const PageRoutes = () => {
    const flags = useFlags();
    // `flags` only changes reference when LaunchDarkly's state actually
    // changes (e.g. identify() resolving) — see useFlags()'s implementation —
    // so this still rebuilds the router whenever flags genuinely change, just
    // not on every unrelated re-render.
    const memoizedRouter = useMemo(() => router(flags), [flags]);
    return (
        <Suspense fallback={<Spinner sizing="largest" />}>
            <RouterProvider router={memoizedRouter} />
        </Suspense>
    );
};

export default PageRoutes;
